import { useEffect, useState } from 'react';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { addVersionKeys, setToggledTitleSearchMode, addVersionSelection } from '@/redux/slices/orderPlacementSlice';
import styled from '@emotion/styled';
import { Button, Card, Grid, IconButton, MenuItem, Select } from '@mui/material';
import ManualPasting from './ManualPasting';
import FileOrPaste from './FileOrPaste';
import { bulkSearchTitles } from '@/redux/slices/bulkSearchSlice';
import { selectBulkTitleLoading } from '@/redux/selectors/bulkSearchSelectors';
import { TitleEntity } from '@/redux/slices/slice_types';
import { useToast } from '@/components/_common/Toast';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { OrderPlacementConfig } from '@/__configs/config_types';

const parsePastedValue = (value: string) => {
  return value.split(',').map(item => item.trim()).filter(item => item !== '');
}

enum ViewMode {
  FILE = 'file',
  PASTE = 'paste',
}

const MAX_PASTE_LENGTH = 500;

const CSVSearch = () => {
  const dispatch = useAppDispatch();
  const bulkTitleSearchOptions = useConfigValue<OrderPlacementConfig['bulkTitleSearchOptions']>(['orderPlacementConfig', 'bulkTitleSearchOptions']);
  const loading = useAppSelector(selectBulkTitleLoading);
  const [pasted, setPasted] = useState<string[]>([]);
  const [idType, setIdType] = useState<string>('RLID');
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.FILE);
  const { error } = useToast();

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      event.preventDefault();
      const pastedData = event.clipboardData?.getData('text');
      if (pastedData) {
        handleSetPasted([...pasted, ...parsePastedValue(pastedData)]);
      }
    };

    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [pasted]);

  const filterVersionTitleEntities = (data: TitleEntity[]) => {
    return data.filter((item: TitleEntity) => item.entity_type === 'version');
  }

  const handleSetPasted = (ids: string[]) => {
    if (ids.length > MAX_PASTE_LENGTH) {
      error(`You can only search for up to ${MAX_PASTE_LENGTH} IDs at a time.`);
      return;
    }
    setPasted(ids);
  }

  const onSave = async () => {
    try {
      dispatch(setToggledTitleSearchMode('default'))

      if (!pasted.length) {
        return;
      }

      const data = await dispatch(bulkSearchTitles({ ids: pasted, id_type: idType })).unwrap();

      if (data) {
        const lineage = new Set<string>();
        const filtered = filterVersionTitleEntities(data?.titles || []);

        dispatch(addVersionKeys(filtered));
        filtered.forEach((item: TitleEntity) => {
          item.lineage.forEach((id) => lineage.add(String(id)));
        });
        filtered.forEach((item) => {
          dispatch(addVersionSelection(item));
        });
      }
    } catch (err) {
      console.log(err);
      error('Error fetching titles. Please try again.');
    }
  };

  const getMainContent = () => {
    if (pasted.length > 0 || viewMode === ViewMode.PASTE) {
      return <ManualPasting values={pasted} onChange={handleSetPasted} />;
    } else {
      return <FileOrPaste onChange={handleSetPasted} onClick={() => setViewMode(ViewMode.PASTE)} />;
    }
  };

  return (
    <Container>
      <TitleSection>
        <div>All values must be followed by a comma and limited to 500 IDs</div>
        <IconButton onClick={() => dispatch(setToggledTitleSearchMode('default'))}>
          <PolarisIcon icon="close" />
        </IconButton>
      </TitleSection>
      <MainContent>
        <IDTypeSelect value={idType} defaultValue={bulkTitleSearchOptions?.keys()?.next()?.value} onChange={(e) => setIdType(e.target.value as string)}>
          {Array.from(bulkTitleSearchOptions?.entries() || []).map(([key, value]: [string, string]) => (
            <MenuItem key={key} value={key}>{value}</MenuItem>
          ))}
        </IDTypeSelect>
        {getMainContent()}
        <Grid container sx={{ mt: 3, justifyContent: 'end' }}>
          <Button disabled={loading} variant="text" color="default" onClick={() => setPasted([])}>Clear</Button>
          <Button disabled={loading} onClick={onSave} variant="outlined" color="primary">Submit</Button>
        </Grid>
      </MainContent>
    </Container>
  )
};

const Container = styled(Card)`
  background-color: ${props => props.theme.palette.background.paper};
  padding: ${props => props.theme.spacing(4)};
`;

const MainContent = styled.div`
  position: relative;
`;

const IDTypeSelect = styled(Select)`
  position: absolute;
  top: ${props => props.theme.spacing(3)};
  left: ${props => props.theme.spacing(3)};
`;

const TitleSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(2)};
`;

export default CSVSearch;